import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DashboardLayout from "src/layouts/DashboardLayout";
import { Alert } from "@porchpass/ui";
import { useAuth } from "src/context";
import {
  isValidPhoneNumber,
  validEmail,
  addCountryCode,
  stripCountryCode,
  isValidPhoneNumberWithCountryCode,
  dealerStorage,
} from "src/utils";
import { Footer } from "./Footer";
import { Button, Input } from "@porchpass/ui";
import { StyledLink } from "src/components/common";

// WARNING:
//
// Because I know someone will eventually want to do this,
// never take text straight from the URL and display it to
// the user. Ever. Ever ever.
//
// Always key it to a known/hardcoded string
//
// -- Past Joe
const getTextForErrorMessageId = (id: string): string => {
  switch (id) {
    case "session-inactivity":
      return "For your security, we logged you out due to inactivity. Please log back in to continue.";
    case "expired-magiclink":
      return "Your login magic link has expired or it has already been used, please try to login again";
    default:
      return "There's been an error authenticating your account. Please retry, or talk to a sales representative to fix.";
  }
};

const hasLoginUrl = () => window.location.pathname.endsWith("/login");

export default () => {
  const { dealerName: rawDealerName } = useParams();
  const dealerName = rawDealerName?.trim();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const loginPrefill = searchParams.get("prefill");
  const formattedPrefill =
    loginPrefill && isValidPhoneNumberWithCountryCode(loginPrefill)
      ? stripCountryCode(loginPrefill)
      : loginPrefill;
  const [inputValue, SetInputValue] = useState(formattedPrefill || "");
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const authObject = useAuth();
  const errorMessageId = searchParams.get("error");
  const redirect = searchParams.get("redirect");
  const isPhoneNumber = isValidPhoneNumber(inputValue);

  useEffect(() => {
    if (authObject.user) {
      if (authObject.user.email) {
        dealerStorage.setItem("email-for-register", authObject.user.email);
      }
      if (authObject.user.phoneNumber) {
        dealerStorage.setItem(
          "phone-for-register",
          authObject.user.phoneNumber,
        );
      }
      navigate(`/${dealerName}/dashboard`, { replace: true });
    }
  }, [authObject.user]);

  const handleLogin = () => {
    if (!inputValue) {
      setIsError(true);
      return;
    }
    // email
    if (validEmail(inputValue)) {
      setIsLoading(true);
      setIsError(false);
      createMagicLink();
      return;
    }
    // phone
    if (isPhoneNumber) {
      setIsLoading(true);
      setIsError(false);
      createSMSCode();
      return;
    }

    setIsError(true);
  };

  const createSMSCode = () => {
    authObject.sendSMSCode(
      addCountryCode(inputValue),
      () => {
        const smsRoute = `/${dealerName}/login/sms-sent${redirect?.length ? `?redirect=${redirect}` : ""}`;
        dealerStorage.setItem("phone-for-sign-in", inputValue);
        window.localStorage.setItem("publicRedirect", smsRoute);
        navigate(smsRoute);
      },
      () => {
        setIsLoading(false);
      },
    );
  };

  const createMagicLink = () => {
    authObject
      .createMagicLink(inputValue, dealerName ?? "")
      .then(() => {
        dealerStorage.setItem("email-for-sign-in", inputValue);
        navigate(`/${dealerName}/login/email-sent`);
      })
      .catch((error) => {
        console.log("Magic Link ERROR :: ", error);
      })
      .finally(() => setIsLoading(false));
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    SetInputValue(event.target.value.trim());
  };

  const isLoginPage = hasLoginUrl();
  return (
    <DashboardLayout pageTitle="Login">
      <div
        className="box-border fixed top-1/2 left-1/2 flex w-full max-w-[450px] grow flex-col items-center gap-0 px-5 text-center -translate-x-1/2 -translate-y-1/2"
        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleLogin();
          }
        }}
      >
        <div
          className="mt-12 mb-5 text-center text-[28px] font-medium"
          data-replay-safe
        >
          {isLoginPage ? "Welcome back" : "Ready to finance your home?"}
        </div>
        <div
          className="mb-7 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-[3px] text-center text-sm font-light text-[#4a5466]"
          data-replay-safe
        >
          {isLoginPage
            ? "To get started, please sign in"
            : "Let's start with your email or phone number"}
        </div>
        {errorMessageId && (
          <Alert variant="error">
            {getTextForErrorMessageId(errorMessageId)}
          </Alert>
        )}
        <div
          className={`w-full self-stretch${isError ? " wiggle-animation" : ""}`}
        >
          <Input
            className="w-full"
            label="Email or phone"
            value={inputValue}
            error={isError}
            helperText={isError ? "Invalid email or phone number" : undefined}
            onChange={onChange}
            onFocus={() => setIsError(false)}
            onBlur={(evt) => {
              const trimmedValue = evt.target.value.trim();
              onChange(evt);
              setIsError(!isPhoneNumber && !validEmail(trimmedValue));
            }}
          />
        </div>
        <Button
          type="button"
          variant="primary"
          size="m"
          className="mt-8 mb-5 w-full"
          onClick={handleLogin}
          loading={isLoading}
        >
          {isLoginPage ? "Continue" : "Get started"}
        </Button>
        {!isLoginPage && (
          <span className="flex gap-[3px] text-sm font-light text-[#4a5466]">
            Already started?{" "}
            <StyledLink to={`/${dealerName}/login`}>Log in</StyledLink>
          </span>
        )}
      </div>
      <Footer />
      <div id="recaptcha-container"></div>
    </DashboardLayout>
  );
};
